/**
 * VAIR 7-Player Algorithm
 *
 * Based on official VAIR 7-Player Score Card v4.15.25
 *
 * Structure:
 * - 7 total games (single round)
 * - 1 court (matches played sequentially)
 * - 3 players have byes each game
 * - Each player plays 4 games total (sits out 3 games)
 * - Subset rotation (not everyone partners with everyone)
 *
 * Note: With 7 players, only 14 of 21 possible partnerships occur.
 * Each player partners with exactly 4 others (not all 6).
 */
import { VairRound, VairSchedule } from './types';

/**
 * Generate VAIR schedule for 7 players
 * Based on official VAIR 7-Player Score Card
 */
export function generateVairSchedule7Players(timePerMatch: number = 20): VairSchedule {
  let currentTime = 0;
  const rounds: VairRound[] = [];

  // Game 1: 1-6 vs 2-5 (Players 3,4,7 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 5 }, // Players 1-6 (0-indexed: 0, 5)
        team2: { player1: 1, player2: 4 }, // Players 2-5 (0-indexed: 1, 4)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [2, 3, 6], // Players 3, 4, 7 (0-indexed: 2, 3, 6)
  });
  currentTime += timePerMatch;

  // Game 2: 5-7 vs 1-4 (Players 2,3,6 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 4, player2: 6 }, // Players 5-7 (0-indexed: 4, 6)
        team2: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [1, 2, 5], // Players 2, 3, 6 (0-indexed: 1, 2, 5)
  });
  currentTime += timePerMatch;

  // Game 3: 2-7 vs 3-6 (Players 1,4,5 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 6 }, // Players 2-7 (0-indexed: 1, 6)
        team2: { player1: 2, player2: 5 }, // Players 3-6 (0-indexed: 2, 5)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0, 3, 4], // Players 1, 4, 5 (0-indexed: 0, 3, 4)
  });
  currentTime += timePerMatch;

  // Game 4: 3-5 vs 2-6 (Players 1,4,7 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 4 }, // Players 3-5 (0-indexed: 2, 4)
        team2: { player1: 1, player2: 5 }, // Players 2-6 (0-indexed: 1, 5)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0, 3, 6], // Players 1, 4, 7 (0-indexed: 0, 3, 6)
  });
  currentTime += timePerMatch;

  // Game 5: 1-3 vs 4-7 (Players 2,5,6 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        team2: { player1: 3, player2: 6 }, // Players 4-7 (0-indexed: 3, 6)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [1, 4, 5], // Players 2, 5, 6 (0-indexed: 1, 4, 5)
  });
  currentTime += timePerMatch;

  // Game 6: 4-6 vs 3-7 (Players 1,2,5 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 5 }, // Players 4-6 (0-indexed: 3, 5)
        team2: { player1: 2, player2: 6 }, // Players 3-7 (0-indexed: 2, 6)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [0, 1, 4], // Players 1, 2, 5 (0-indexed: 0, 1, 4)
  });
  currentTime += timePerMatch;

  // Game 7: 4-2 vs 1-5 (Players 3,6,7 bye)
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 1 }, // Players 4-2 (0-indexed: 3, 1)
        team2: { player1: 0, player2: 4 }, // Players 1-5 (0-indexed: 0, 4)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [2, 5, 6], // Players 3, 6, 7 (0-indexed: 2, 5, 6)
  });

  return { rounds };
}

/**
 * Validate 7-player VAIR schedule
 * - Each player should play in exactly 4 games (sit out 3)
 * - 3 players should have byes each game
 * - Total of 7 games
 */
export function validateVairSchedule7Players(schedule: VairSchedule): boolean {
  const playerGameCount: Map<number, number> = new Map();
  const playerByeCount: Map<number, number> = new Map();

  // Initialize
  for (let i = 0; i < 7; i++) {
    playerGameCount.set(i, 0);
    playerByeCount.set(i, 0);
  }

  // Count games and byes per player
  schedule.rounds.forEach((round) => {
    // Count active players
    round.matches.forEach((match) => {
      const allPlayers = [
        match.team1.player1,
        match.team1.player2,
        match.team2.player1,
        match.team2.player2,
      ];

      allPlayers.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
      });
    });

    // Count byes
    round.byes.forEach((player) => {
      playerByeCount.set(player, (playerByeCount.get(player) || 0) + 1);
    });
  });

  // Validate: Each player should play 4 games
  const expectedGames = 4;
  for (let i = 0; i < 7; i++) {
    const gamesPlayed = playerGameCount.get(i) || 0;
    if (gamesPlayed !== expectedGames) {
      console.error(
        `[7p VAIR] Player ${i + 1} played ${gamesPlayed} games, expected ${expectedGames}`,
      );
      return false;
    }
  }

  // Validate: Each player should have exactly 3 byes
  const expectedByes = 3;
  for (let i = 0; i < 7; i++) {
    const byes = playerByeCount.get(i) || 0;
    if (byes !== expectedByes) {
      console.error(`[7p VAIR] Player ${i + 1} had ${byes} byes, expected ${expectedByes}`);
      return false;
    }
  }

  // Validate: Should have 7 rounds total
  if (schedule.rounds.length !== 7) {
    console.error(`[7p VAIR] Expected 7 rounds, got ${schedule.rounds.length}`);
    return false;
  }

  // Validate: Each round should have exactly 1 match and 3 byes
  const invalidRounds = schedule.rounds.filter(
    (round) => round.matches.length !== 1 || round.byes.length !== 3,
  );
  if (invalidRounds.length > 0) {
    console.error(`[7p VAIR] Expected 1 match and 3 byes per round, found invalid rounds`);
    return false;
  }

  return true;
}
